import DropdownQuestionGroup from '../Shared/Dropdowns/DropdownQuestionGroup'
import { useCallback, useEffect, useState } from 'react';

import { Option, Question, QuestionGroup, Skill } from '../../Models';
import DropdownQuestionType from '../Shared/Dropdowns/DropDownQuestionType';
import CheckboxInput from '../CheckboxInput';
import { ChevronRightIcon, PlusCircleIcon } from '@heroicons/react/20/solid';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { formats, toolbar } from '../../helpers/constants';
import { useNotification } from '../../context/NotificationContext';
import Skilldropdown from './Dropdowns/SkillDropdown';
import { AddQuestionTime, fetchQuestionGroups, getQuestionById, GetQuestionTimes, postQuestion, postQuestionGroup, putAndPostAssessmentOptions, putAssessmentQuestion, UpdateIsObsolete, UpdateQuestionTime, uploadQuestionImage } from '../../api/assessmentquestion.api';
import { fetchAllSkills, getQuestionSkill, postQuestionSkill, postSkill } from '../../api/skill.api';
import useApiToken from '../../hooks/useApiToken';

export default function AddEditQuestion() {
    const token = useApiToken();
    const [selectedQType, setSelectedQType] = useState<Question>({
        id: '1',
        title: 'SINGLE'
    });
    const [selectedQGroup, setSelectedQGroup] = useState<QuestionGroup>()
    const [options, setOptions] = useState<Option[] | undefined>()
    const [text, setText] = useState('');
    const [time, setTime] = useState('');
    const [tags, setTags] = useState('');
    const [setupScript, setSetupScript] = useState('');
    const [expectedQuery, setExpectedQuery] = useState('');
    const [initialCode, setInitialCode] = useState('');
    const [testCases, setTestCases] = useState('[\n  {\n    "input": "1, 2",\n    "expectedOutput": "3",\n    "isHidden": false\n  }\n]');
    const [skills, setSkills] = useState<Skill[]>([]);
    const [isDialogOpen, setIsDialogOpen] = useState({ skill: false, group: false });
    const [newGroupName, setNewGroupName] = useState<string>('');
    const [newSkillName, setNewSkillName] = useState<Skill>();
    const { success, error: notifyError } = useNotification();
    const [selectedSkills, setSelectedSkills] = useState<Skill[]>([]);
    const [questionGroups, setQuestionGroups] = useState<QuestionGroup[]>([]);
    const [questionTimes, setQuestionTimes] = useState<
        { id: string; time: number; accuracy: Number; isObsolete: boolean; questionDisplayTime?: number }[]
    >([]);
    const [showNewTimeInput, setShowNewTimeInput] = useState(false);
    const [originalQuestionTimes, setOriginalQuestionTimes] = useState<
        { id: string; time: number; accuracy: number; isObsolete: boolean; questionDisplayTime?: number }[]
    >([]);
    const [newTime, setNewTime] = useState<number | "">("");
    const [questionImageUrl, setQuestionImageUrl] = useState<string>('');
    const [uploadingQuestionImage, setUploadingQuestionImage] = useState(false);
    const { id } = useParams();
    const navigate = useNavigate();

    useEffect(() => {
        if (id && token) {
            const getDetails = async () => {
                await getQuestionById(id, token).then((res) => {
                    setText(res.text);
                    setTags(res.tags);
                    setTime(res.time);
                    const qType: string = res.type.toUpperCase()
                    const type: Question = {
                        id: (Math.random()).toString(),
                        title: qType
                    };
                    setSelectedQType(type);
                    setOptions(res.answers)
                    const group: QuestionGroup = res.questionGroup;
                    setSelectedQGroup(group);
                    setSetupScript(res.setupScript || '');
                    setExpectedQuery(res.expectedQuery || '');
                    setInitialCode(res.initialCode || '');
                    setTestCases(res.testCases || '[\n  {\n    "input": "1, 2",\n    "expectedOutput": "3",\n    "isHidden": false\n  }\n]');
                    if (res.imageUrl) {
                        setQuestionImageUrl(res.imageUrl);
                    }
                })
                await getQuestionSkill(id, token).then((res) => {
                    setSelectedSkills(res);
                });
            }
            getDetails();
            getQuestionTimes(id, token);
        }
    }, [id, token]);
    const getQuestionTimes = async (id: string, token: string) => {
        const res = await GetQuestionTimes(id, token);
        if (res) {
            setQuestionTimes(res);
            setOriginalQuestionTimes(JSON.parse(JSON.stringify(res)));
        }
    };

    const getAllQuestionGroups = useCallback(() => {
        if (!token) return;
        fetchQuestionGroups(token).then((res) => {
            setQuestionGroups(res);
        });
    }, [token])
    useEffect(() => {
        getAllQuestionGroups();
    }, [getAllQuestionGroups]);
    const getAllSkills = () => {
        if (!token) return;
        fetchAllSkills(token).then((res) => setSkills(res));
    }
    useEffect(() => {
        getAllSkills();
    }, [token])

    const setSelectedQuestionGroup = (selected: QuestionGroup | undefined) => {
        setSelectedQGroup(selected)
    }

    const setSelectedQuestionType = (selected: Question) => {
        setSelectedQType(selected);
    }

    const getOptionsfromChild = (options: Option[]) => {
        setOptions(options);
    }

    const handleQuestionSubmit = async (e: any) => {
        e.preventDefault();
        if (!token) return;
        const selectedIsCorrect = options?.filter((o) => { return o.isCorrect === true })
        const optionText = options?.filter((o) => { return o.text.trim() === '' });
        if (optionText && optionText?.length > 0) {
            notifyError('Error', 'Option field cannot be empty');
        }
        else if (selectedQType.title === 'MULTIPLE' && selectedIsCorrect && selectedIsCorrect.length <= 1) {
            notifyError('Error', 'More than options need to be selected when question type is multiple');
        }
        else if (selectedQType.title === 'SINGLE' && selectedIsCorrect && selectedIsCorrect.length < 1) {
            notifyError('Error', 'Atleast one option need to be selected when question type is single');
        }
        else if (selectedQType.title === 'IMAGE' && selectedIsCorrect && selectedIsCorrect.length < 1) {
            notifyError('Error', 'Atleast one option need to be selected when question type is image');
        }
        else if (selectedQType.title === 'ROLE QUESTION' && selectedIsCorrect && selectedIsCorrect.length > 0) {
            notifyError('Error', 'No correct option can be selected for role question type');
        }
        else {
            if (selectedQGroup?.id && (options || selectedQType.title === 'TEXT' || selectedQType.title === 'VIDEO' || selectedQType.title === 'COMPREHENSIVE' || selectedQType.title === 'SQL' || selectedQType.title === 'JAVASCRIPT' || selectedQType.title === 'REACT') && text && time && tags) {
                const question = await postQuestion(text, time, tags, null, selectedQGroup?.id, selectedQType.title, options as Option[] | undefined, null, token, null, setupScript, expectedQuery, initialCode, testCases,questionImageUrl || null);
                 await AddQuestionTime(question.id, parseFloat(time), 50, false, token, undefined);
                if (selectedSkills.length > 0) {
                    postQuestionSkill(question.id, selectedSkills, token);
                }
                success('Success', 'Question Added Successfully');
                setTimeout(() => {
                    navigate('/questions');
                }, 2000)
            }
        }

    }


    const handleQuestionUpdate = (e: any): void => {
        e.preventDefault();
        if (!token) return;
        const selectedType = selectedQType.title;
        const selectedIsCorrect = options?.filter((o) => { return o.isCorrect === true })
        const optionText = options?.filter((o) => { return o.text.trim() === '' });
        if (optionText && optionText?.length > 0) {
            notifyError('Error', 'Option field cannot be empty');
        }
        else if (selectedQType.title === 'MULTIPLE' && selectedIsCorrect && selectedIsCorrect.length <= 1) {
            notifyError('Error', 'More than options need to be selected when question type is multiple');
        }
        else if (selectedQType.title === 'SINGLE' && selectedIsCorrect && selectedIsCorrect.length < 1) {
            notifyError('Error', 'Atleast one option need to be selected when question type is single');
        }
        else if (selectedQType.title === 'IMAGE' && selectedIsCorrect && selectedIsCorrect.length < 1) {
            notifyError('Error', 'Atleast one option need to be selected when question type is image');
        }
        else if (selectedQType.title === 'ROLE QUESTION' && selectedIsCorrect && selectedIsCorrect.length > 0) {
            notifyError('Error', 'No correct option can be selected for role question type');
        }
        else {
            if (selectedQGroup?.id && (options || selectedQType.title === 'TEXT' || selectedQType.title === 'VIDEO' || selectedQType.title === 'COMPREHENSIVE' || selectedQType.title === 'SQL' || selectedQType.title === 'JAVASCRIPT' || selectedQType.title === 'REACT') && text && time && tags) {
                putAssessmentQuestion(id, text, time, selectedType, tags, null, selectedQGroup, null, token, null, setupScript, expectedQuery, initialCode, testCases,  questionImageUrl || null);
                
                if (options) {
                    putAndPostAssessmentOptions(id, options, token);
                }
                postQuestionSkill(id, selectedSkills, token);
                success('Success', 'Question Updated Successfully');
                setTimeout(() => {
                    navigate('/questions');
                }, 2000)
            }
            if (hasQuestionTimesChanged()) {
                handleQuestionTimeUpdate();
                success('Success', 'Question Updated Successfully');
                setTimeout(() => {
                    navigate('/questions');
                }, 2000)
            }
        }
    }


    const openDialog = (type: string) => {
        setIsDialogOpen((prev) => ({
            ...prev,
            [type]: type === 'group' ? !isDialogOpen.group : !isDialogOpen.skill
        }))

    };

    const closeDialog = () => {
        setIsDialogOpen({ skill: false, group: false });
    };

    const handleCreate = async (e: any) => {
        e.preventDefault();
        if (!token) return;
        try {
            await postQuestionGroup(newGroupName, token).then(() => {
                setNewGroupName('');
                closeDialog();
                success('Success', 'Question Group Added');
                setTimeout(() => {
                    getAllQuestionGroups();
                }, 2000)

            })
        } catch (error) {
            notifyError('Error', 'Unable to Add Question Group');
            setTimeout(() => {
                getAllQuestionGroups();
            }, 2000)
        }
    };

    const clearFields = () => {
        setText('');
        setTags('');
        setOptions([]);
        setTime('');
        setQuestionImageUrl('');
    }

    const handleQuestionImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !token) return;

        setUploadingQuestionImage(true);
        try {
            const imageUrl = await uploadQuestionImage(file, token);
            setQuestionImageUrl(imageUrl);
            success('Success', 'Question image uploaded successfully');
        } catch (error) {
            notifyError('Error', 'Failed to upload question image');
        } finally {
            setUploadingQuestionImage(false);
        }
    };

    const onSkillSelect = (currentSelectedSkill: Skill[]) => {
        setSelectedSkills(currentSelectedSkill)
    }

    const handleCreateSkill = async () => {
        if (!token) return;
        await postSkill(newSkillName, token);
        await getAllSkills();
        closeDialog();
        setNewSkillName({
            id: '',
            name: ''
        });
    }

        const hasQuestionTimesChanged = (): boolean => {
        return questionTimes.some((qt) => {
          const originalQt = originalQuestionTimes.find((o) => o.id === qt.id);
    
        
          if (!originalQt) return true;
    
          
          if (
            originalQt.time !== qt.time ||
            originalQt.isObsolete !== qt.isObsolete ||
            originalQt.questionDisplayTime !== qt.questionDisplayTime
          ) {
            return true;
          }
    
          return false;
        });
      };
        const handleQuestionTimeUpdate = async () => {
        if(!id || !token){
            return ;
        }
          for (const qt of questionTimes) {
            const originalQt = originalQuestionTimes.find((o) => o.id === qt.id);
      
            const hasTimeChanged = originalQt && originalQt.time !== qt.time;
            const hasDisplayTimeChanged = originalQt && originalQt.questionDisplayTime !== qt.questionDisplayTime;
           
            if (qt.id.includes("new-")) {
              await AddQuestionTime(
                id,
                qt.time,
                50,
                qt.isObsolete,
                token,
                qt.questionDisplayTime
              );
            }
            else if (hasTimeChanged || hasDisplayTimeChanged) {
              await UpdateQuestionTime(
                id,
                qt.id,
                qt.time,
                qt.questionDisplayTime,
                token
              );
            }
            else if (originalQt && originalQt.isObsolete !== qt.isObsolete) {
              await UpdateIsObsolete(qt.id, qt.isObsolete, token);
            }
          }
      
          setOriginalQuestionTimes(JSON.parse(JSON.stringify(questionTimes)));
        };
     
    const toggleIsObsolete = (id: string, checked: boolean) => {
        setQuestionTimes((prev) => {

            return prev.map((item) =>
                item.id === id ? { ...item, isObsolete: checked } : item
            );
        });
    };

    return (
        <>
            <header className="bg-gray-100 py-8 dark:bg-darkbglight">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 xl:flex xl:items-center xl:justify-between">
                    <div className="min-w-0 flex-1">
                        <nav className="flex" aria-label="Breadcrumb">
                            <ol className="flex items-center space-x-4">
                                <li>
                                    <div>
                                        <Link
                                            to="/questions"
                                            className="text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
                                        >
                                            Questions
                                        </Link>
                                    </div>
                                </li>
                                <li>
                                    <div className="flex items-center">
                                        <ChevronRightIcon
                                            className="h-5 w-5 flex-shrink-0 text-gray-400 dark:text-gray-400"
                                            aria-hidden="true"
                                        />
                                        <span className="ml-4 text-sm font-medium text-gray-500 dark:text-gray-400">
                                            {id ? 'Edit ' : 'Create '}
                                        </span>
                                    </div>
                                </li>
                            </ol>
                        </nav>
                        <h1 className="mt-2 text-2xl font-bold leading-7 text-gray-800 sm:text-3xl sm:tracking-tight dark:text-gray-300">
                            {id ? 'Edit Question' : 'Create New Question'}
                        </h1>
                    </div>
                </div>
            </header>
            <form className="space-y-12 sm:space-y-16 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 ">
                <div className=" p-6 ">
                    <div className="flex justify-between py-14 border-b border-gray-300 dark:border-gray-600">
                        <div className='flex items-start flex-col gap-8 lg:flex-row'>
                            <div className="cursor-pointer sm:w-fit lg:w-fit mx-2">
                                <small className='block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400'>Question Group *</small>
                                <div className='flex flex-row gap-2 text-xs '>
                                    <DropdownQuestionGroup group={questionGroups} setSelectedQuestionGroup={setSelectedQuestionGroup} selectedQGroup={selectedQGroup} />
                                    {!id && <div className='flex flex-row relative marker items-center justify-between '>
                                        {
                                            isDialogOpen.group && <div className='border border-gray-300 p-2 h-36 flex flex-col justify-between rounded-md absolute z-10 bg-white  dark:bg-darkbg dark:border-gray-600 text-xs lg:left-6 lg:-bottom-3/3 lg:top-0 right-3 top-16 w-fit'>
                                                <div>
                                                    <div className=' block sm:text-sm font-medium text-gray-700 w-64 text-left mb-2 dark:text-gray-300 text-sm'>Create Question Group</div>
                                                    <div>
                                                        <input type="text" className='w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:border-gray-600 dark:text-gray-400' placeholder='Enter Group Name' onChange={(e) => setNewGroupName(e.target.value)} />
                                                    </div>
                                                </div>
                                                <div className='flex flex-row justify-between'>
                                                    <button className='text-sm font-semibold text-gray-900 mr-4 bg-gray-300 px-4 py-2 rounded-md' onClick={(e) => { closeDialog(); setNewGroupName('') }}>Cancel</button>
                                                    <button className='bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ' onClick={handleCreate}>Create </button>
                                                </div>
                                            </div>
                                        }
                                        <PlusCircleIcon height={20} color='blue' onClick={() => openDialog('group')} className={`cursor-pointer ${isDialogOpen.group ? 'origin-center rotate-45 ease-in duration-100' : ''}`}
                                        />
                                    </div>}
                                </div>
                            </div>
                            <div className="cursor-pointer sm:w-fit lg:w-fit mx-2">
                                <small className='block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400'>Question Type *</small>
                                <DropdownQuestionType setSelectedQuestionType={setSelectedQuestionType} selectedQType={selectedQType} />
                            </div>
                            <div className="cursor-pointer sm:w-fit lg:w-fit mx-2">
                                <small className='block sm:text-sm font-medium text-gray-700 w-64 text-left dark:text-gray-400'>Skill Type</small>
                                <div className='flex flex-row gap-2'>
                                    <Skilldropdown skills={skills} selectedSkills={selectedSkills} onSkillsSelect={onSkillSelect} />
                                    {!id && <div className='flex flex-row relative marker items-center justify-between'>
                                        {
                                            isDialogOpen.skill && <div className='border border-gray-300 p-2 h-36 flex flex-col justify-between rounded-md absolute z-10 bg-white  dark:bg-darkbg dark:border-gray-600 text-xs lg:left-6 lg:-bottom-3/3 lg:top-0 right-3 top-16 w-fit'>
                                                <div>
                                                    <div className=' block sm:text-sm font-medium text-gray-700 w-64 text-left mb-2 dark:text-gray-300 text-sm'>Create Skill </div>
                                                    <div>
                                                        <input type="text" className='w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:border-gray-600 dark:text-gray-400' placeholder='Enter Skill Name' onChange={(e) => setNewSkillName({
                                                            id: '',
                                                            name: e.target.value
                                                        })} />
                                                    </div>
                                                </div>
                                                <div className='flex flex-row justify-between'>
                                                    <button className='text-sm font-semibold text-gray-900 mr-4 bg-gray-300 px-4 py-2 rounded-md' onClick={(e) => {
                                                        e.preventDefault();
                                                        closeDialog(); setNewSkillName({
                                                            id: '',
                                                            name: ''
                                                        })
                                                    }}>Cancel</button>
                                                    <button className='bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ' onClick={(e) => {
                                                        e.preventDefault();
                                                        handleCreateSkill();
                                                    }}>Create</button>
                                                </div>
                                            </div>
                                        }
                                        <PlusCircleIcon height={20} color='blue' onClick={() => openDialog('skill')} className={`cursor-pointer ${isDialogOpen.skill ? 'origin-center rotate-45 ease-in duration-100' : ''}`}
                                        />
                                    </div>}
                                </div>
                            </div>

                        </div>


                    </div>
                    <div className='flex flex-col items-start py-10 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="questionText" className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:text-gray-400">
                            Question Text *
                        </label>

                        <div className='w-full'>
                            <ReactQuill
                                value={text}
                                onChange={setText}
                                formats={formats}
                                modules={{
                                    toolbar: {
                                        container: toolbar,
                                    }
                                }}
                                className='w-full rounded-md p-2 focus:outline-none focus:ring focus:border-indigo-500 h-fit text-left dark:text-gray-400 dark:border-gray-600'
                                id="questionText"
                            />

                            {selectedQType.title === 'IMAGE' && (
                                <div className='mt-6'>
                                    <label className="block text-sm font-medium text-gray-700 mb-2 dark:text-gray-400">
                                        Question Image (Optional)
                                    </label>
                                    <input
                                        type="file"
                                        accept="image/*"
                                        onChange={handleQuestionImageUpload}
                                        disabled={uploadingQuestionImage}
                                        className="block w-full text-sm text-gray-500 file:mr-2 file:py-2 file:px-3 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100 dark:text-gray-400 dark:file:bg-indigo-900 dark:file:text-indigo-200"
                                    />
                                    {uploadingQuestionImage && (
                                        <p className="text-sm text-gray-500 mt-2 dark:text-gray-400">Uploading...</p>
                                    )}
                                    {questionImageUrl && (
                                        <div className="mt-3">
                                            <img
                                                src={questionImageUrl}
                                                alt="Question"
                                                className="max-w-md rounded border border-gray-300 dark:border-gray-600"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setQuestionImageUrl('')}
                                                className="mt-2 text-sm text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                                            >
                                                Remove Image
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                    </div>

                    {(selectedQType.title === 'SINGLE' ||
                        selectedQType.title === 'MULTIPLE' ||
                        selectedQType.title === 'ROLE QUESTION' ||
                        selectedQType.title === 'IMAGE') && (
                            <CheckboxInput
                                selectedQType={selectedQType}
                                getOptionsfromChild={getOptionsfromChild}
                                selectedOptions={options}
                                isQuestionsModalOpen={false}
                            />
                        )}

                    {id ? (
                        <div className="flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600 ">
                            <div className="w-full">
                                <label className="block text-sm font-medium text-gray-700 py-4 dark:bg-transparent dark:text-gray-400">
                                    Question Times
                                </label>

                                <ul className="space-y-2">
                                    {questionTimes.map((qt) => (
                                        <li
                                            key={qt.id}
                                            className="flex items-center text-gray-900 dark:text-gray-400"
                                        >
                                            <input
                                                type="checkbox"
                                                checked={qt.isObsolete}
                                                onChange={(e) => {
                                                    toggleIsObsolete(
                                                        qt.id,
                                                        !qt.isObsolete
                                                    );
                                                }}
                                                className="mr-2"
                                            />
                                            <span
                                                className={
                                                    qt.isObsolete
                                                        ? "line-through text-gray-500 dark:text-gray-400"
                                                        : "text-gray-900 dark:text-gray-400"
                                                }
                                            >
                                                {qt.time +
                                                    " Seconds | Difficulty : " +
                                                    qt.accuracy + "%"}
                                            </span>
                                        </li>
                                    ))}
                                </ul>

                                {!showNewTimeInput ? (
                                    <button
                                        onClick={() =>
                                            setShowNewTimeInput(true)
                                        }
                                        className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
                                    >
                                        Add New Time
                                    </button>
                                ) : (
                                    <div className="mt-4">
                                        <input
                                            type="number"
                                            placeholder="Enter time in seconds"
                                            value={newTime}
                                            onChange={(e) =>
                                                setNewTime(
                                                    e.target.value
                                                        ? Number(e.target.value)
                                                        : ""
                                                )
                                            }
                                            className="w-full rounded-md border-gray-300 p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                                        />
                                        <button
                                            onClick={() => {
                                                if (newTime) {
                                                    setQuestionTimes((prev) => {

                                                        if (prev.some((qt) => qt.time === newTime)) {
                                                            return prev;
                                                        }

                                                        return [
                                                            ...prev,
                                                            {
                                                                id: "new-" + Date.now().toString(),
                                                                time: newTime,
                                                                accuracy: 50,
                                                                isObsolete: false,
                                                            },
                                                        ];
                                                    });
                                                    setNewTime("");
                                                    setShowNewTimeInput(false);
                                                }
                                            }}
                                            className="mt-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                        >
                                            Save
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (<div className='flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="questionTime" className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:bg-transparent dark:text-gray-400">
                            Time <small>(In seconds) </small>  *
                        </label>
                        <input
                            id="questionTime"
                            name="questionTime"
                            type="number"
                            className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                            onChange={(e) => setTime(e.target.value)}
                            min={0}
                            value={time}
                            onWheel={(e) => e.currentTarget.blur()}
                        />
                    </div>)}

                    {selectedQType.title === 'SQL' && (
                        <>
                            <div className='flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                                <label className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:bg-transparent dark:text-gray-400">
                                    Database Schema Setup Script
                                </label>
                                <textarea
                                    className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-32"
                                    value={setupScript}
                                    onChange={(e) => setSetupScript(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === " " || e.code === "Space") {
                                        e.preventDefault();
                                        e.stopPropagation();
                                      }
                                    }}
                                    placeholder="CREATE TABLE Employee...&#10;INSERT INTO Employee..."
                                />
                            </div>
                            <div className='flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                                <label className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:bg-transparent dark:text-gray-400">
                                    Expected SQL Query
                                </label>
                                <textarea
                                    className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-32"
                                    value={expectedQuery}
                                    onChange={(e) => setExpectedQuery(e.target.value)}
                                      onKeyDown={(e) => {
                                        if (e.key === " " || e.code === "Space") {
                                          e.preventDefault();
                                          e.stopPropagation();
                                        }
                                      }}
                                    placeholder="SELECT * FROM Employee..."
                                />
                            </div>
                        </>
                    )}

                    {selectedQType.title === 'JAVASCRIPT' && (
                        <>
                            <div className='flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                                <label className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:bg-transparent dark:text-gray-400">
                                    Initial Code (Boilerplate)
                                </label>
                                <textarea
                                    className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-32"
                                    value={initialCode}
                                    onChange={(e) => setInitialCode(e.target.value)}
                                    placeholder="function solution(a, b) {&#10;  // Write code here&#10;}"
                                />
                            </div>
                            <div className='flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                                <label className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:bg-transparent dark:text-gray-400">
                                    Test Cases (JSON Array)
                                </label>
                                <textarea
                                    className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-48 font-mono text-sm"
                                    value={testCases}
                                    onChange={(e) => setTestCases(e.target.value)}
                                    placeholder='[{"input": "1, 2", "expectedOutput": "3", "isHidden": false}]'
                                />
                            </div>
                        </>
                    )}

                    {selectedQType.title === 'REACT' && (
                        <>
                            <div className='flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                                <label className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:bg-transparent dark:text-gray-400">
                                    Component Template
                                </label>
                                <textarea
                                    className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-32 font-mono text-sm"
                                    value={initialCode}
                                    onChange={(e) => setInitialCode(e.target.value)}
                                    placeholder="export default function MyComponent() {&#10;  return <div>Hello World</div>;&#10;}"
                                />
                            </div>
                            <div className='flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                                <label className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:bg-transparent dark:text-gray-400">
                                    Test Cases (JSON Array)
                                </label>
                                <textarea
                                    className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600 h-48 font-mono text-sm"
                                    value={testCases}
                                    onChange={(e) => setTestCases(e.target.value)}
                                    placeholder='[{"input": "{}", "expectedOutput": "<div>Hello World</div>", "isHidden": false}]'
                                />
                            </div>
                        </>
                    )}

                    <div className='flex flex-col items-start py-14 border-b border-gray-300 lg:flex-row dark:border-gray-600'>
                        <label htmlFor="questionTags" className="block text-sm font-medium text-gray-700 w-64 text-left py-4 dark:text-gray-400">
                            Tags *
                        </label>
                        <input
                            id="questionTags"
                            name="questionTags"
                            type="text"
                            className="w-full rounded-md border-gray-300 border p-2 focus:outline-none focus:ring focus:border-indigo-500 dark:bg-transparent dark:text-gray-400 dark:border-gray-600"
                            onChange={(e) => setTags(e.target.value)}
                            value={tags}
                        />
                    </div>
                </div>

                <div className="mt-8 flex justify-end">
                    <button type="button" className="text-sm font-semibold text-gray-900 mr-4  dark:text-gray-100" onClick={clearFields}>
                        Clear
                    </button>
                    {
                        id ? <>
                            <button
                                onClick={handleQuestionUpdate}
                                type="submit"
                                className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ${selectedQGroup?.id && (options || selectedQType.title === 'TEXT' || selectedQType.title === 'VIDEO' || selectedQType.title === 'COMPREHENSIVE' || selectedQType.title === 'SQL' || selectedQType.title === 'JAVASCRIPT' || selectedQType.title === 'REACT') && text.length > 0 && time && time.toString().length > 0 && tags.length > 0
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                                disabled={!(selectedQGroup?.id && (options || selectedQType.title === 'TEXT' || selectedQType.title === 'VIDEO' || selectedQType.title === 'COMPREHENSIVE' || selectedQType.title === 'SQL' || selectedQType.title === 'JAVASCRIPT' || selectedQType.title === 'REACT') && text.length > 0 && time && time.toString().length > 0 && tags.length > 0)}
                            >
                                Update
                            </button>

                        </> : <>
                            <button
                                onClick={handleQuestionSubmit}
                                type="submit"
                                className={`rounded-md px-4 py-2 text-sm font-semibold shadow-sm focus:outline-none focus:ring focus:border-indigo-500 ${selectedQGroup?.id && (options || selectedQType.title === 'TEXT' || selectedQType.title === 'VIDEO' || selectedQType.title === 'COMPREHENSIVE' || selectedQType.title === 'SQL' || selectedQType.title === 'JAVASCRIPT' || selectedQType.title === 'REACT') && text.length > 0 && time.toString().length > 0 && tags.length > 0
                                    ? 'bg-indigo-600 text-white hover:bg-indigo-500 cursor-pointer'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    }`}
                                disabled={!(selectedQGroup?.id && (options || selectedQType.title === 'TEXT' || selectedQType.title === 'VIDEO' || selectedQType.title === 'COMPREHENSIVE' || selectedQType.title === 'SQL' || selectedQType.title === 'JAVASCRIPT' || selectedQType.title === 'REACT' ) && text.length > 0 && time.toString().length > 0 && tags.length > 0)}
                            >
                                Submit
                            </button>

                        </>
                    }
                </div>
            </form>
        </>
    );
}
